import { sample } from 'openapi-sampler';
import { OpenAPIV3 } from 'openapi-types';
import { AppModel } from '../../shared/models/config.model';
import {
  httpMethods,
  OpenApiCollectionParameter,
  OpenApiCollectionRequest,
  OpenApiCollectionRequestBody,
  OpenApiHttpMethod,
} from '../models/api-collection.model';

export function getCollectionName(app: AppModel, document: OpenAPIV3.Document): string {
  return document.info.title.trim() || app.id;
}

export function getBaseUrl(app: AppModel, document: OpenAPIV3.Document): string {
  return stripTrailingSlash(document.servers?.[0]?.url ?? app.serverUrl);
}

export function getResource(app: AppModel, document: OpenAPIV3.Document): string {
  const baseUrl = getBaseUrl(app, document);
  const url = new URL(baseUrl);
  return url.origin;
}

export function getOpenApiCollectionRequests(app: AppModel, document: OpenAPIV3.Document): OpenApiCollectionRequest[] {
  const baseUrl = getBaseUrl(app, document);

  return Object.entries(document.paths).flatMap(([path, pathItem]) => {
    if (!pathItem || isReferenceObject(pathItem)) {
      return [];
    }

    return httpMethods.flatMap((method) => {
      const operation = pathItem[method];

      if (!operation) {
        return [];
      }

      const clientPath = toClientPath(path);
      const parameters = getParameters(path, pathItem, operation);
      const queryParameters = parameters.filter((parameter) => parameter.in === 'query').map(toCollectionParameter);
      const pathParameters = parameters.filter((parameter) => parameter.in === 'path').map(toCollectionParameter);

      return [
        {
          id: operation.operationId ?? `${method}-${path}`,
          name: getOperationName(method, path, operation),
          method: toHttpMethod(method),
          path: clientPath,
          url: `${baseUrl}${clientPath}`,
          description: operation.description ?? operation.summary,
          queryParameters,
          pathParameters,
          body: getRequestBody(operation, document),
          tags: operation.tags ?? [],
        },
      ];
    });
  });
}

export function buildExportFileName(name: string, suffix: string): string {
  const sanitizedName = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${sanitizedName || 'api'}${suffix}`;
}

const int32 = new Int32Array(1);

function wrapToSignedInt32(value: number): number {
  int32[0] = value;
  return int32[0];
}

export function createStableUid(value: string, index: number): string {
  let hash = 0;

  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;
    hash = wrapToSignedInt32(Math.imul(hash, 31) + codePoint);
  }

  const normalizedHash = Math.abs(hash).toString(36);

  return `${normalizedHash}${index.toString(36)}000000000000000000000`.slice(0, 21);
}

function getOperationName(method: OpenApiHttpMethod, path: string, operation: OpenAPIV3.OperationObject): string {
  return operation.operationId ?? operation.summary ?? `${method.toUpperCase()} ${path}`;
}

function toHttpMethod(method: OpenApiHttpMethod): Uppercase<OpenApiHttpMethod> {
  switch (method) {
    case 'get':
      return 'GET';
    case 'put':
      return 'PUT';
    case 'post':
      return 'POST';
    case 'delete':
      return 'DELETE';
    case 'options':
      return 'OPTIONS';
    case 'head':
      return 'HEAD';
    case 'patch':
      return 'PATCH';
    case 'trace':
      return 'TRACE';
  }
}

function getParameters(
  path: string,
  pathItem: OpenAPIV3.PathItemObject,
  operation: OpenAPIV3.OperationObject,
): OpenAPIV3.ParameterObject[] {
  const configuredParameters = [...(pathItem.parameters ?? []), ...(operation.parameters ?? [])]
    .filter((parameter): parameter is OpenAPIV3.ParameterObject => !isReferenceObject(parameter))
    .filter((parameter) => parameter.in === 'query' || parameter.in === 'path');

  const configuredPathParameterNames = new Set(
    configuredParameters.filter((parameter) => parameter.in === 'path').map((parameter) => parameter.name),
  );

  const pathParameters = [...path.matchAll(/\{([^}]+)\}/g)]
    .map((match) => match[1])
    .filter((name): name is string => !!name)
    .filter((name) => !configuredPathParameterNames.has(name))
    .map(
      (name) =>
        ({
          name,
          in: 'path',
          required: true,
          schema: { type: 'string' },
        }) satisfies OpenAPIV3.ParameterObject,
    );

  return [...configuredParameters, ...pathParameters];
}

function toCollectionParameter(parameter: OpenAPIV3.ParameterObject): OpenApiCollectionParameter {
  return {
    name: parameter.name,
    value: `{{${parameter.name}}}`,
    description: parameter.description,
  };
}

function getRequestBody(
  operation: OpenAPIV3.OperationObject,
  document: OpenAPIV3.Document,
): OpenApiCollectionRequestBody {
  if (!operation.requestBody || isReferenceObject(operation.requestBody)) {
    return { mode: 'none' };
  }

  const content = operation.requestBody.content;
  const jsonContentType = Object.keys(content).find((contentType) => contentType.includes('json'));

  if (jsonContentType) {
    return {
      mode: 'raw',
      contentType: jsonContentType,
      value: getRawBodyValue(content[jsonContentType], document, jsonContentType),
    };
  }

  const formUrlEncoded = content['application/x-www-form-urlencoded'];

  if (formUrlEncoded) {
    return {
      mode: 'formUrlEncoded',
      contentType: 'application/x-www-form-urlencoded',
      parameters: getFormUrlEncodedParameters(formUrlEncoded, document),
    };
  }

  const firstContentType = Object.keys(content)[0];

  if (!firstContentType) {
    return { mode: 'none' };
  }

  return {
    mode: 'raw',
    contentType: firstContentType,
    value: getRawBodyValue(content[firstContentType], document, firstContentType),
  };
}

function getRawBodyValue(
  mediaType: OpenAPIV3.MediaTypeObject,
  document: OpenAPIV3.Document,
  contentType: string,
): string {
  if (mediaType.example !== undefined) {
    return stringifyBodyValue(mediaType.example);
  }

  const example = Object.values(mediaType.examples ?? {}).find(
    (example): example is OpenAPIV3.ExampleObject => !isReferenceObject(example) && example.value !== undefined,
  );

  if (example) {
    return stringifyBodyValue(example.value);
  }

  if (mediaType.schema) {
    return stringifyBodyValue(getSchemaSampleValue(mediaType.schema, document, contentType));
  }

  return '{}';
}

function stringifyBodyValue(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function getFormUrlEncodedParameters(
  mediaType: OpenAPIV3.MediaTypeObject,
  document: OpenAPIV3.Document,
): OpenApiCollectionParameter[] {
  if (!mediaType.schema) {
    return [];
  }

  const sampledBody = getSchemaSampleValue(mediaType.schema, document, 'application/json');

  if (!isRecord(sampledBody)) {
    return [];
  }

  return Object.entries(sampledBody).map(([name, value]) => ({
    name,
    value: stringifyParameterValue(value),
  }));
}

function getSchemaSampleValue(
  schemaOrReference: OpenAPIV3.ReferenceObject | OpenAPIV3.SchemaObject,
  document: OpenAPIV3.Document,
  contentType: string,
): unknown {
  try {
    return sample(
      schemaOrReference,
      {
        format: contentType.includes('xml') ? 'xml' : 'json',
        quiet: true,
        skipReadOnly: true,
      },
      document,
    );
  } catch {
    return {};
  }
}

function stringifyParameterValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  return JSON.stringify(value);
}

function toClientPath(path: string): string {
  return path.replace(/\{([^}]+)\}/g, ':$1');
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/g, '');
}

function isReferenceObject(value: unknown): value is OpenAPIV3.ReferenceObject {
  return typeof value === 'object' && value !== null && '$ref' in value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
